// Vendor imports
const _ = require('lodash')
const { createSelector } = require('reselect')
// Shared imports
const nameUtils = require('shared/name-utils')
// Client V2 common imports
const contentAddModalSelectors = require('client-v2/common/containers/content-add-modal/selectors')
const collectionModelRecord = require('client-v2/common/models/collection/record')
const { FetchStateEnum } = require('client-v2/common/utils/enums')

const containerState = state => state.collectionSelectModal

const isVisible = createSelector(
  containerState,
  s => Boolean(s && s.isVisible)
)

const anchorId = createSelector(
  containerState,
  s => _.get(s, 'anchorId')
)

const article = createSelector(
  containerState,
  s => _.get(s, 'article')
)

const urlId = createSelector(
  article,
  a => _.get(a, 'url_id')
)

const pageContextRecord = createSelector(
  containerState,
  s => _.get(s, 'pageContext')
)

const pageContextToJS = createSelector(
  pageContextRecord,
  r => r ? r.toJS() : null
)

const isContentAddModalVisible = contentAddModalSelectors.isVisible

const isLightweightModalVisible = createSelector(
  [isVisible, anchorId, isContentAddModalVisible],
  (isVisible, anchorId, isContentAddModalVisible) => {
    return Boolean(isVisible && (anchorId || isContentAddModalVisible))
  }
)

const collections = createSelector(
  containerState,
  s => {
    if (s && s.collections) {
      return s.collections.filter(item => {
        /**
         * Strip out "recommended" collections that don't actually exist.
         * Lightweight engagement does not have designs for "suggested" collections.
         * @see https://app.asana.com/0/1121261655472708/1124199149549158/f
         */
        return !!item.id
      })
    }
  }
)

const url = createSelector(
  [
    containerState,
    article
  ],
  (s, a) => _.get(s, 'url') || _.get(a, 'url')
)

const isArticleFailed = createSelector(
  containerState,
  s => Boolean(s && s.articleState === FetchStateEnum.FAILED)
)

const isArticleLoaded = createSelector(
  containerState,
  s => Boolean(s && s.articleState === FetchStateEnum.LOADED)
)

const isArticleUnmixable = createSelector(
  article,
  a => Boolean(a && a.isUnmixable)
)

const isCollectionsFailed = createSelector(
  containerState,
  s => Boolean(s && s.collectionsState === FetchStateEnum.FAILED)
)

const isCollectionsLoaded = createSelector(
  containerState,
  s => Boolean(s && s.collectionsState === FetchStateEnum.LOADED)
)

const isCreatePending = createSelector(
  containerState,
  s => Boolean(s && s.createState === FetchStateEnum.LOADING)
)

const curatedCollectionIds = createSelector(
  collections,
  collections => {
    if (collections) {
      const collectionIds = []
      collections.forEach(item => {
        if (item.includesUrl) {
          collectionIds.push(item.id)
        }
      })
      return collectionIds
    }
  }
)

const curatedCollectionCountsByVisibility = createSelector(
  collections,
  collections => {
    if (collections) {
      const counts = { public: 0, private: 0 }
      collections.forEach(item => {
        if (item.includesUrl) {
          if (item.visibility === collectionModelRecord.VISIBILITY_PRIVATE) {
            counts.private += 1
          } else {
            counts.public += 1
          }
        }
      })
      return counts
    }
  }
)

const isUnmixPending = createSelector(
  containerState,
  s => Boolean(s && s.unmixState === FetchStateEnum.LOADING)
)

const isUnmixPromptVisible = createSelector(
  containerState,
  s => Boolean(s && s.isUnmixPromptVisible)
)

const filterText = createSelector(
  containerState,
  s => s ? s.filterText : ''
)

const canCreateFromFilter = createSelector(
  filterText,
  filterText => nameUtils.isValidMixName(filterText)
)

const filterSlug = createSelector(
  filterText,
  filterText => nameUtils.prettyUrl(filterText)
)

const filterSlugMatcher = createSelector(
  filterSlug,
  filterSlug => {
    const startsWith = filterSlug.length < 2 ? '^' : ''
    const escapedStr = _.escapeRegExp(filterSlug)
    const regexp = new RegExp(startsWith + escapedStr, 'i')
    return itemSlug => regexp.test(itemSlug)
  }
)

const filteredCollections = createSelector(
  [
    filterSlugMatcher,
    collections
  ],
  (filterSlugMatcher, collections) => {
    if (collections) {
      return collections.filter(item => filterSlugMatcher(item.slug))
    }
  }
)

const filteredCollectionsCount = createSelector(
  filteredCollections,
  filteredCollections => {
    if (filteredCollections) {
      return filteredCollections.size
    }
  }
)

const isFilteredCollectionsEmpty = createSelector(
  filteredCollectionsCount,
  filteredCollectionsCount => filteredCollectionsCount === 0
)

const isUnfilteredCollectionsEmpty = createSelector(
  collections,
  collections => {
    if (collections) {
      return collections.size === 0
    }
  }
)

module.exports = {
  anchorId,
  article,
  canCreateFromFilter,
  collections,
  containerState,
  curatedCollectionIds,
  curatedCollectionCountsByVisibility,
  filterSlug,
  filterText,
  filteredCollections,
  filteredCollectionsCount,
  isArticleFailed,
  isArticleLoaded,
  isArticleUnmixable,
  isCollectionsFailed,
  isCollectionsLoaded,
  isContentAddModalVisible,
  isCreatePending,
  isFilteredCollectionsEmpty,
  isLightweightModalVisible,
  isUnfilteredCollectionsEmpty,
  isUnmixPending,
  isUnmixPromptVisible,
  isVisible,
  pageContextRecord,
  pageContextToJS,
  url,
  urlId
}
